import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Wallet as WalletIcon, CreditCard, ArrowUpRight, ArrowDownRight, Clock,
  Plus, TrendingUp, Zap, Check, X, Loader2, ShieldCheck,
  Download, FileText, Crown, ChevronRight, AlertTriangle,
  Sparkles, Rocket, Filter, Calendar, Search, RefreshCw,
  ArrowUp, ArrowDown, ExternalLink, Receipt, PiggyBank, Scale,
  ChevronDown, ChevronUp, Eye, XCircle
} from 'lucide-react';
import { walletAPI, paymentAPI, membershipAPI, invoiceAPI, subscriptionAPI } from '../../api';
import { format, parseISO, subDays } from 'date-fns';
import toast from 'react-hot-toast';
import { useRazorpay } from 'react-razorpay';
import { formatCredits, formatBalance } from '../../utils/credits';

// Balance Card Component
const BalanceCard = ({ title, value, icon: Icon, gradient, subtitle, trend }) => (
  <div className={`bg-gradient-to-br ${gradient} rounded-2xl p-5 text-white`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-white/80 text-sm font-medium">{title}</p>
        <p className="text-3xl font-bold mt-1">{formatBalance(value)}</p>
        {subtitle && <p className="text-white/70 text-xs mt-1">{subtitle}</p>}
      </div>
      <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
    {trend !== undefined && (
      <div className="mt-3 flex items-center gap-1 text-white/80 text-sm">
        {trend >= 0 ? <TrendingUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
        <span>{Math.abs(trend)}% from last month</span>
      </div>
    )}
  </div>
);

// Savings Calculator Component
const SavingsCalculator = ({ savings, currentPlan, onUpgrade }) => {
  if (!savings || savings.length === 0) return null;
  
  const bestPlan = savings.find(s => s.is_upgrade_beneficial) || savings[0];
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-200 overflow-hidden"
    >
      <div className="p-5 border-b border-amber-200/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center">
              <PiggyBank className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Potential Savings</h3>
              <p className="text-sm text-slate-600">Based on your last 30 days usage</p>
            </div>
          </div>
          {bestPlan.is_upgrade_beneficial && (
            <span className="px-3 py-1 bg-emerald-500 text-white text-xs font-semibold rounded-full">
              You Could Save!
            </span>
          )}
        </div>
      </div>
      
      <div className="p-5">
        {savings.map((plan, index) => (
          <div 
            key={plan.plan}
            className={`${index > 0 ? 'mt-4 pt-4 border-t border-amber-200/50' : ''}`}
          >
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              {/* Plan Info */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="font-semibold text-slate-900">
                    Upgrade to {plan.plan_name}
                  </h4>
                  <span className="px-2 py-0.5 bg-slate-200 text-slate-600 text-xs rounded">
                    ₹{plan.monthly_subscription?.toLocaleString()}/mo
                  </span>
                </div>
                
                {/* Usage comparison */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm mb-3">
                  <div className="bg-white/50 rounded-lg p-2">
                    <p className="text-slate-500 text-xs">Your 30d Try-Ons</p>
                    <p className="font-semibold text-slate-800">{plan.your_30d_tryon_count}</p>
                  </div>
                  <div className="bg-white/50 rounded-lg p-2">
                    <p className="text-slate-500 text-xs">Your 30d Moments</p>
                    <p className="font-semibold text-slate-800">{plan.your_30d_moments_count}</p>
                  </div>
                  <div className="bg-white/50 rounded-lg p-2">
                    <p className="text-slate-500 text-xs">Current Cost</p>
                    <p className="font-semibold text-slate-800">₹{plan.your_30d_cost_current?.toLocaleString()}</p>
                  </div>
                  <div className="bg-white/50 rounded-lg p-2">
                    <p className="text-slate-500 text-xs">With {plan.plan_name}</p>
                    <p className="font-semibold text-emerald-600">₹{plan.your_30d_cost_upgraded?.toLocaleString()}</p>
                  </div>
                </div>
                
                {/* Benefits */}
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                    VTO: ₹{plan.tryon_cost}/call
                  </span>
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
                    Moments: ₹{plan.moments_cost}/call
                  </span>
                  <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full">
                    +₹{plan.monthly_credits_included?.toLocaleString()} usage balance/mo
                  </span>
                </div>
              </div>
              
              {/* Savings & CTA */}
              <div className="flex flex-col items-end gap-2 flex-shrink-0">
                {plan.net_monthly_savings > 0 ? (
                  <div className="text-right">
                    <p className="text-2xl font-bold text-emerald-600">
                      Save ₹{plan.net_monthly_savings?.toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-500">per month</p>
                  </div>
                ) : plan.credits_saved_per_month > 0 ? (
                  <div className="text-right">
                    <p className="text-lg font-bold text-blue-600">
                      {plan.percent_saved_on_calls}% cheaper calls
                    </p>
                    <p className="text-xs text-slate-500">
                      Save {plan.credits_saved_per_month} credits/mo
                    </p>
                  </div>
                ) : (
                  <div className="text-right">
                    <p className="text-sm text-slate-500">
                      Great for scaling up
                    </p>
                  </div>
                )}
                <button
                  onClick={() => onUpgrade(plan.plan)}
                  className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                    plan.is_upgrade_beneficial
                      ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                      : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                  }`}
                >
                  {plan.is_upgrade_beneficial ? 'Upgrade & Save' : 'View Plan'}
                </button>
              </div>
            </div>
          </div>
        ))}
        
        {/* Note about subscription bonus */}
        <div className="mt-4 pt-4 border-t border-amber-200/50">
          <p className="text-xs text-amber-700 flex items-start gap-2">
            <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              <strong>Bonus:</strong> Subscribers also get {currentPlan === 'starter' ? '200' : '200'} extra credits 
              added to their account each month as a loyalty bonus!
            </span>
          </p>
        </div>
      </div>
    </motion.div>
  );
};

// Transaction Row Component
const TransactionRow = ({ tx, onDownloadInvoice, downloadingInvoice }) => {
  const isCredit = tx.credits > 0;
  const isPending = tx.status === 'pending' || tx.status === 'verified';
  const isFailed = tx.status === 'failed' || tx.status === 'cancelled';
  
  const typeLabels = {
    recharge: 'Credit Top-Up',
    subscription_bonus: 'Subscription Bonus',
    subscription_upgrade: 'Subscription Plan Credits',
    referral_bonus: 'Referral Bonus',
    admin_credit: 'Admin Credit',
    admin_debit: 'Admin Debit',
    admin_adjustment: 'Admin Adjustment',
    credit_adjustment: 'Manual Credit',
    debit_adjustment: 'Manual Debit',
    usage: tx.api_type === 'size_recommendation' ? 'Size Recommendation Usage' : `${tx.api_type?.toUpperCase() || 'API'} Usage`,
    signup_bonus: 'Welcome Bonus',
    promotional_credit: 'Promotional Credit',
    refund: 'Payment Refund'
  };

  // Check if this is an In-Store TryOn transaction (partner has no access to output)
  const isInStoreTryOn = tx.source === 'In-Store TryOn' || tx.is_in_store;

  // Get source description
  const getSourceDescription = () => {
    if (tx.type === 'recharge') {
      if (tx.razorpay_payment_id) return `Razorpay Payment`;
      return 'Payment Gateway';
    }
    if (tx.type === 'referral_bonus') return 'Referral Program';
    if (tx.type === 'signup_bonus') return 'New Account';
    if (tx.type === 'subscription_upgrade' || tx.type === 'subscription_bonus') {
      return tx.plan_id ? `${tx.plan_id.charAt(0).toUpperCase() + tx.plan_id.slice(1)} Plan` : 'Subscription';
    }
    if (tx.type?.includes('admin') || tx.type?.includes('adjustment')) {
      return tx.admin_email || 'Admin Action';
    }
    // Handle In-Store TryOn source explicitly
    if (tx.source === 'In-Store TryOn' || tx.is_in_store) {
      return 'In-Store TryOn';
    }
    if (tx.type === 'usage') {
      if (tx.api_type === 'size_recommendation') return 'Size Recommendation API';
      return `${tx.api_type?.toUpperCase() || 'API'} API`;
    }
    // Use source from transaction if available
    if (tx.source && tx.source !== 'public_studio') return tx.source;
    return '';
  };

  // Don't show pending/failed recharge transactions as successful credits
  const showAsCredit = isCredit && !isPending && !isFailed;
  const sourceDesc = getSourceDescription();

  return (
    <div className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-xl transition-colors gap-3 ${
      isPending ? 'bg-amber-50 border border-amber-200' : 
      isFailed ? 'bg-red-50 border border-red-200' :
      'bg-slate-50 hover:bg-slate-100'
    }`}>
      <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
          isPending ? 'bg-amber-100' :
          isFailed ? 'bg-red-100' :
          showAsCredit ? 'bg-emerald-100' : 'bg-amber-100'
        }`}>
          {isPending ? (
            <Clock className="w-5 h-5 text-amber-600" />
          ) : isFailed ? (
            <XCircle className="w-5 h-5 text-red-600" />
          ) : showAsCredit ? (
            <ArrowUpRight className="w-5 h-5 text-emerald-600" />
          ) : (
            <ArrowDownRight className="w-5 h-5 text-amber-600" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium text-slate-900">
              {typeLabels[tx.type] || tx.type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </p>
            {sourceDesc && (
              <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">
                {sourceDesc}
              </span>
            )}
            {isPending && (
              <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full font-medium">
                Pending
              </span>
            )}
            {isFailed && (
              <span className="text-xs bg-red-200 text-red-800 px-2 py-0.5 rounded-full font-medium">
                Failed
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-500 mt-1">
            <span>{tx.created_at ? format(parseISO(tx.created_at), 'MMM d, yyyy HH:mm') : '-'}</span>
            {tx.invoice_number && (
              <span className="text-blue-600 font-mono text-xs bg-blue-50 px-1.5 py-0.5 rounded">{tx.invoice_number}</span>
            )}
            {tx.sku_id && (
              <span className="text-slate-400 text-xs">SKU: {tx.sku_id}</span>
            )}
            {tx.cached && (
              <span className="text-green-600 text-xs bg-green-50 px-1.5 py-0.5 rounded">Cached</span>
            )}
            {tx.razorpay_payment_id && (
              <span className="text-purple-600 text-xs font-mono">{tx.razorpay_payment_id.slice(0, 14)}...</span>
            )}
          </div>
          {tx.description && (tx.type?.includes('admin') || tx.type?.includes('adjustment')) && (
            <p className="text-xs text-slate-400 mt-0.5 truncate">Note: {tx.description}</p>
          )}
          {isPending && tx.type === 'recharge' && (
            <p className="text-xs text-amber-600 mt-1">Payment not completed - credits will be added after successful payment</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3 sm:gap-4 justify-between sm:justify-end">
        <div className="text-right">
          <p className={`font-semibold text-lg ${
            isPending ? 'text-amber-600' :
            isFailed ? 'text-red-400 line-through' :
            showAsCredit ? 'text-emerald-600' : 'text-amber-600'
          }`}>
            {isPending ? '⏳ ' : ''}{showAsCredit ? '+' : ''}{formatCredits(tx.credits)}
          </p>
          {tx.amount > 0 && <p className="text-sm text-slate-500">₹{tx.amount.toLocaleString()}</p>}
        </div>
        <div className="flex items-center gap-1">
          {tx.type === 'recharge' && !isPending && !isFailed && (
            <button
              onClick={() => onDownloadInvoice(tx.transaction_id)}
              disabled={downloadingInvoice === tx.transaction_id}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Download Invoice"
            >
              {downloadingInvoice === tx.transaction_id ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Receipt className="w-5 h-5" />
              )}
            </button>
          )}
          {tx.operation_id && !isInStoreTryOn && (
            <a
              href={`/partners/gallery?operation=${tx.operation_id}`}
              className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="View Operation"
            >
              <ExternalLink className="w-5 h-5" />
            </a>
          )}
          {isInStoreTryOn && (
            <span 
              className="p-2 text-amber-500" 
              title="In-Store outputs are visible only to customers"
            >
              <Eye className="w-5 h-5 opacity-50" />
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const Wallet = () => {
  const { Razorpay } = useRazorpay();
  const [wallet, setWallet] = useState(null);
  const [walletSummary, setWalletSummary] = useState(null);
  const [savingsData, setSavingsData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [recharging, setRecharging] = useState(false);
  const [showRecharge, setShowRecharge] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentStep, setPaymentStep] = useState('select');
  const [membership, setMembership] = useState(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [minRechargeAmount, setMinRechargeAmount] = useState(500);  // Configurable min amount
  
  // Filter and pagination state
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [customDateStart, setCustomDateStart] = useState('');
  const [customDateEnd, setCustomDateEnd] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  const ITEMS_PER_PAGE = 20;

  const fetchData = async () => {
    try {
      const [walletRes, packagesRes, membershipRes, summaryRes, savingsRes, plansRes] = await Promise.all([
        walletAPI.get(),
        walletAPI.getCreditPackages(),
        membershipAPI.getStatus(),
        walletAPI.getSummary(),
        walletAPI.getSavingsCalculator().catch(() => ({ data: null })),
        subscriptionAPI.getPlans().catch(() => ({ data: { plans: [] } }))
      ]);
      setWallet(walletRes.data);
      setWalletSummary(summaryRes.data);
      setSavingsData(savingsRes.data);
      setSubscriptionPlans(plansRes.data.plans || []);
      // Set min recharge amount from API or use default
      if (packagesRes.data.min_recharge_amount) {
        setMinRechargeAmount(packagesRes.data.min_recharge_amount);
      }
      setPackages(packagesRes.data.packages || [
        { id: 'pkg_500', credits: 500, price: 500, popular: false },
        { id: 'pkg_1000', credits: 1000, price: 1000, popular: true },
        { id: 'pkg_2000', credits: 2000, price: 2000, popular: false },
        { id: 'pkg_5000', credits: 5000, price: 5000, popular: false }
      ]);
      setMembership(membershipRes.data);
      if (packagesRes.data.packages?.length > 0) {
        const popularPkg = packagesRes.data.packages.find(p => p.popular);
        setSelectedPackage(popularPkg || packagesRes.data.packages[0]);
      }
    } catch (error) {
      toast.error('Failed to load wallet');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = useCallback(async () => {
    setLoadingTransactions(true);
    try {
      const params = {
        limit: ITEMS_PER_PAGE,
        offset: (page - 1) * ITEMS_PER_PAGE,
        sort_by: sortBy,
        sort_order: sortOrder
      };
      
      if (typeFilter !== 'all') {
        params.type_filter = typeFilter;
      }
      
      // Handle date filter
      if (dateFilter === '7d') {
        params.start_date = format(subDays(new Date(), 7), 'yyyy-MM-dd');
      } else if (dateFilter === '30d') {
        params.start_date = format(subDays(new Date(), 30), 'yyyy-MM-dd');
      } else if (dateFilter === '90d') {
        params.start_date = format(subDays(new Date(), 90), 'yyyy-MM-dd');
      } else if (dateFilter === 'custom' && customDateStart) {
        params.start_date = customDateStart;
        if (customDateEnd) params.end_date = customDateEnd;
      }
      
      const res = await walletAPI.getTransactions(params);
      setTransactions(res.data.transactions || []);
      setTotalTransactions(res.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setLoadingTransactions(false);
    }
  }, [page, typeFilter, dateFilter, customDateStart, customDateEnd, sortBy, sortOrder]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const openRechargeModal = () => {
    setShowRecharge(true);
    setPaymentStep('select');
    setCustomAmount('');
    const popularPkg = packages.find(p => p.popular);
    setSelectedPackage(popularPkg || packages[0]);
  };

  const closeRechargeModal = () => {
    setShowRecharge(false);
    setPaymentStep('select');
  };

  const openUpgradeModal = (plan = null) => {
    setShowUpgrade(true);
    setSelectedPlan(plan || 'growth');
    setPaymentStep('select');
  };

  const closeUpgradeModal = () => {
    setShowUpgrade(false);
    setPaymentStep('select');
  };

  const getAmount = () => {
    if (customAmount && parseInt(customAmount) >= minRechargeAmount) {
      const credits = parseInt(customAmount);
      return { credits, price: credits };  // 1:1 pricing
    }
    if (customAmount && parseInt(customAmount) > 0 && parseInt(customAmount) < minRechargeAmount) {
      return { credits: 0, price: 0, error: `Minimum ${minRechargeAmount} credits required` };
    }
    return selectedPackage || { credits: 0, price: 0 };
  };

  const currentAmount = getAmount();
  const gstAmount = Math.round(currentAmount.price * 0.18);
  const totalWithGST = currentAmount.price + gstAmount;

  const handleDownloadInvoice = async (transactionId) => {
    setDownloadingInvoice(transactionId);
    try {
      const response = await invoiceAPI.download(transactionId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${transactionId.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Invoice downloaded');
    } catch (error) {
      toast.error('Failed to download invoice');
    } finally {
      setDownloadingInvoice(null);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = {};
      if (typeFilter !== 'all') params.type_filter = typeFilter;
      if (dateFilter === '7d') params.start_date = format(subDays(new Date(), 7), 'yyyy-MM-dd');
      else if (dateFilter === '30d') params.start_date = format(subDays(new Date(), 30), 'yyyy-MM-dd');
      else if (dateFilter === '90d') params.start_date = format(subDays(new Date(), 90), 'yyyy-MM-dd');
      else if (dateFilter === 'custom' && customDateStart) {
        params.start_date = customDateStart;
        if (customDateEnd) params.end_date = customDateEnd;
      }
      
      const res = await walletAPI.exportTransactions(params);
      const data = res.data.data;
      
      if (data.length === 0) {
        toast.error('No data to export');
        return;
      }
      
      // Enhanced CSV with full audit trail
      const headers = [
        'Date',
        'Transaction ID', 
        'Type',
        'Source',
        'Credits',
        'Credit/Debit',
        'Amount (INR)',
        'GST (INR)',
        'Total (INR)',
        'Status',
        'API Type',
        'SKU ID',
        'Invoice Number',
        'Razorpay Payment ID',
        'Notes'
      ];
      
      const csvContent = [
        headers.join(','),
        ...data.map(row => [
          row.date || '',
          row.transaction_id || '',
          `"${row.type_description || row.type || ''}"`,
          `"${row.source || ''}"`,
          row.credits || 0,
          row.credit_debit || '',
          row.amount_inr || 0,
          row.gst_amount || 0,
          row.total_amount || 0,
          row.status_display || row.status || '',
          row.api_type || '',
          row.sku_id || '',
          row.invoice_number || '',
          row.razorpay_payment_id || '',
          `"${(row.notes || '').replace(/"/g, '""')}"`
        ].join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nanokart-credit-audit-${format(new Date(), 'yyyy-MM-dd-HHmm')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      
      // Show summary
      const summary = res.data.summary || {};
      toast.success(`Exported ${data.length} transactions! Total credits: +${summary.total_credits || 0}, Debits: -${summary.total_debits || 0}`);
    } catch (error) {
      toast.error('Failed to export');
    } finally {
      setExporting(false);
    }
  };

  const handleSubscriptionUpgrade = async () => {
    if (!selectedPlan) return;
    
    // Get plan price from subscription plans data
    const planData = subscriptionPlans.find(p => p.plan_id === selectedPlan);
    const planPrice = planData ? (planData.effective_price || planData.price_monthly_intro || planData.price_monthly) : 0;
    
    if (!planPrice || planPrice <= 0) {
      toast.error('Unable to determine plan price. Please try from the Subscription page.');
      return;
    }
    
    setRecharging(true);
    setPaymentStep('processing');

    try {
      const orderRes = await paymentAPI.createOrder({
        amount: planPrice,  // Send amount in rupees, backend handles paise conversion
        currency: 'INR',
        order_type: 'subscription',
        plan: selectedPlan,
        billing_cycle: 'monthly'
      });

      const { order_id, key_id, mock, amount: razorpayAmount } = orderRes.data;

      if (mock) {
        await new Promise(resolve => setTimeout(resolve, 1500));
        await paymentAPI.verifyPayment({
          razorpay_order_id: order_id,
          razorpay_payment_id: `pay_mock_${Date.now()}`,
          razorpay_signature: 'mock_signature',
          order_type: 'subscription'
        });
        setPaymentStep('success');
        setTimeout(() => {
          closeUpgradeModal();
          fetchData();
          fetchTransactions();
          toast.success(`Upgraded to ${planData?.name || selectedPlan}!`, { duration: 5000 });
        }, 2000);
      } else {
        const options = {
          key: key_id,
          amount: razorpayAmount,  // Use amount from backend (already in paise)
          currency: 'INR',
          name: 'Nanokart.ai',
          description: `${planData?.name || selectedPlan} Plan - Monthly`,
          order_id: order_id,
          handler: async (response) => {
            try {
              setPaymentStep('processing');
              await paymentAPI.verifyPayment({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                order_type: 'subscription'
              });
              setPaymentStep('success');
              setTimeout(() => {
                closeUpgradeModal();
                fetchData();
                fetchTransactions();
                toast.success(`Upgraded to ${planData?.name || selectedPlan}!`, { duration: 5000 });
              }, 2000);
            } catch (err) {
              toast.error(err.response?.data?.detail || 'Payment verification failed');
              setPaymentStep('select');
            }
          },
          modal: { ondismiss: () => { setPaymentStep('select'); setRecharging(false); } },
          theme: { color: '#2563EB' }
        };
        setPaymentStep('select');
        const razorpayInstance = new Razorpay(options);
        razorpayInstance.open();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
      setPaymentStep('select');
    } finally {
      setRecharging(false);
    }
  };

  const handlePayment = async () => {
    const amount = getAmount();
    if (amount.credits < minRechargeAmount) {
      toast.error(`Minimum ${minRechargeAmount} credits required`);
      return;
    }
    setRecharging(true);
    setPaymentStep('processing');

    try {
      const orderRes = await paymentAPI.createOrder({
        amount: amount.price,
        currency: 'INR',
        order_type: 'recharge',
        package_id: selectedPackage?.id || 'custom'
      });

      const { order_id, key_id, mock, amount: razorpayAmount } = orderRes.data;

      if (mock) {
        await new Promise(resolve => setTimeout(resolve, 1500));
        const verifyRes = await paymentAPI.verifyPayment({
          razorpay_order_id: order_id,
          razorpay_payment_id: `pay_mock_${Date.now()}`,
          razorpay_signature: 'mock_signature',
          order_type: 'recharge'
        });
        setPaymentStep('success');
        setTimeout(() => {
          closeRechargeModal();
          fetchData();
          fetchTransactions();
          const credits = verifyRes.data.credits_added || verifyRes.data.credits_pending || amount.credits;
          toast.success(`${credits} usage balance will be added shortly!`);
        }, 2000);
      } else {
        const options = {
          key: key_id,
          amount: razorpayAmount,  // Use amount from backend (already includes GST, in paise)
          currency: 'INR',
          name: 'Nanokart.ai',
          description: `${amount.credits} Credits`,
          order_id: order_id,
          handler: async (response) => {
            try {
              setPaymentStep('processing');
              const verifyRes = await paymentAPI.verifyPayment({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                order_type: 'recharge'
              });
              setPaymentStep('success');
              setTimeout(() => {
                closeRechargeModal();
                fetchData();
                fetchTransactions();
                // Credits are now added via webhook, show pending message
                const credits = verifyRes.data.credits_added || verifyRes.data.credits_pending || amount.credits;
                toast.success(`Payment successful! ${credits} usage balance will be added shortly.`, { duration: 5000 });
              }, 2000);
            } catch (err) {
              toast.error(err.response?.data?.detail || 'Payment verification failed');
              setPaymentStep('select');
            }
          },
          modal: { ondismiss: () => { setPaymentStep('select'); setRecharging(false); } },
          theme: { color: '#2563EB' }
        };
        setPaymentStep('select');
        const razorpayInstance = new Razorpay(options);
        razorpayInstance.open();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payment failed');
      setPaymentStep('select');
    } finally {
      setRecharging(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const planDisplayName = (plan) => {
    const names = { trial: 'Trial', starter: 'Starter', growth: 'Growth', professional: 'Professional', enterprise: 'Enterprise', basic: 'Starter' };
    return names[plan] || plan;
  };

  const totalPages = Math.ceil(totalTransactions / ITEMS_PER_PAGE);
  const currentPlan = membership?.current_plan || 'starter';
  const isTopTier = currentPlan === 'enterprise';

  // Calculate next tier info from actual subscription plans
  const getNextTierInfo = () => {
    if (!subscriptionPlans.length) {
      // Fallback while loading
      return currentPlan === 'starter' 
        ? { name: 'Growth', price: 999, credits: 1000, plan_id: 'growth' }
        : currentPlan === 'growth'
          ? { name: 'Professional', price: 1999, credits: 2100, plan_id: 'professional' }
          : null;
    }
    
    if (currentPlan === 'starter') {
      const growthPlan = subscriptionPlans.find(p => p.plan_id === 'growth');
      if (growthPlan) {
        return { 
          name: growthPlan.name, 
          price: growthPlan.effective_price || growthPlan.price_monthly_intro || growthPlan.price_monthly, 
          credits: growthPlan.effective_credits || growthPlan.monthly_credits_intro || growthPlan.monthly_credits,
          plan_id: 'growth'
        };
      }
    } else if (currentPlan === 'growth') {
      const proPlan = subscriptionPlans.find(p => p.plan_id === 'professional');
      if (proPlan) {
        return { 
          name: proPlan.name, 
          price: proPlan.effective_price || proPlan.price_monthly_intro || proPlan.price_monthly, 
          credits: proPlan.effective_credits || proPlan.monthly_credits_intro || proPlan.monthly_credits,
          plan_id: 'professional'
        };
      }
    } else if (currentPlan === 'professional') {
      return { name: 'Enterprise', price: null, credits: null, plan_id: 'enterprise', isCustom: true };
    }
    return null;
  };
  const nextTier = getNextTierInfo();

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="wallet-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Usage & Billing</h1>
          <p className="text-slate-600 text-sm mt-1">Manage your usage balance, view transactions, and track spending</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {!isTopTier && (
            <button
              onClick={() => openUpgradeModal(currentPlan === 'starter' ? 'growth' : 'professional')}
              className="px-4 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-colors flex items-center gap-2"
              data-testid="upgrade-plan-btn"
            >
              <Rocket className="w-5 h-5" />
              <span className="hidden sm:inline">Upgrade to {nextTier?.name}</span>
              <span className="sm:hidden">Upgrade</span>
            </button>
          )}
          <button
            onClick={openRechargeModal}
            className="px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
            data-testid="add-credits-btn"
          >
            <Plus className="w-5 h-5" />
            Add Usage
          </button>
        </div>
      </div>

      {/* Grace Period Banner */}
      {membership?.show_renewal_banner && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3"
        >
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-amber-800">
              {membership.subscription_status === 'grace' 
                ? `Your subscription has expired. Renew within ${membership.days_remaining} days to avoid downgrade.`
                : 'Your subscription payment is past due.'}
            </p>
          </div>
          <button
            onClick={() => openUpgradeModal(membership.stored_plan)}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 text-sm flex-shrink-0"
          >
            Renew Now
          </button>
        </motion.div>
      )}

      {/* Current Plan Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`rounded-2xl border p-5 ${
          currentPlan === 'enterprise' ? 'bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200' :
          currentPlan === 'growth' ? 'bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-200' :
          'bg-white border-slate-200'
        }`}
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
              currentPlan === 'enterprise' ? 'bg-purple-500' :
              currentPlan === 'growth' ? 'bg-blue-500' :
              'bg-slate-400'
            }`}>
              <Crown className="w-7 h-7 text-white" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Current Plan</p>
              <p className={`text-2xl font-bold ${
                currentPlan === 'enterprise' ? 'text-purple-600' :
                currentPlan === 'growth' ? 'text-blue-600' :
                'text-slate-600'
              }`}>
                {planDisplayName(currentPlan)}
              </p>
              {membership?.subscription_end_at && currentPlan !== 'starter' && (
                <p className="text-xs text-slate-500">
                  {membership.subscription_status === 'active' ? 'Renews' : 'Expired'}: {format(parseISO(membership.subscription_end_at), 'MMM d, yyyy')}
                </p>
              )}
              {currentPlan === 'starter' && (
                <p className="text-xs text-slate-500">Pay-as-you-go pricing</p>
              )}
            </div>
          </div>
          
          {/* API Pricing */}
          <div className="flex gap-6">
            <div className="text-center">
              <p className="text-xs text-slate-500">Virtual Try-On</p>
              <p className="text-xl font-bold text-slate-900">{membership?.api_pricing?.virtual_tryon || 24}</p>
              <p className="text-xs text-slate-400">₹/call</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-slate-500">Moments</p>
              <p className="text-xl font-bold text-slate-900">{membership?.api_pricing?.moments || 10}</p>
              <p className="text-xs text-slate-400">₹/call</p>
            </div>
          </div>
        </div>
        
        {/* Next tier prompt for non-enterprise users */}
        {nextTier && nextTier.price && (
          <div className="mt-4 pt-4 border-t border-slate-200/50">
            <button
              onClick={() => openUpgradeModal(currentPlan === 'starter' ? 'growth' : 'enterprise')}
              className={`w-full p-3 rounded-lg border transition-colors text-left flex items-center justify-between ${
                currentPlan === 'starter' ? 'bg-blue-50 hover:bg-blue-100 border-blue-200' : 'bg-purple-50 hover:bg-purple-100 border-purple-200'
              }`}
            >
              <div>
                <p className={`font-semibold ${currentPlan === 'starter' ? 'text-blue-700' : 'text-purple-700'}`}>
                  Upgrade to {nextTier.name}  -  ₹{nextTier.price?.toLocaleString()}/mo
                </p>
                <p className={`text-xs ${currentPlan === 'starter' ? 'text-blue-600' : 'text-purple-600'}`}>
                  Includes ₹{nextTier.credits?.toLocaleString()} usage balance/mo + lower per-call pricing
                </p>
              </div>
              <ChevronRight className={`w-5 h-5 ${currentPlan === 'starter' ? 'text-blue-400' : 'text-purple-400'}`} />
            </button>
          </div>
        )}
      </motion.div>

      {/* Balance Cards Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <BalanceCard
          title="Included Balance"
          value={wallet?.included_balance ?? (wallet?.balance - (wallet?.topup_balance || 0))}
          icon={Sparkles}
          gradient="from-emerald-500 to-green-600"
          subtitle="From your plan"
        />
        <BalanceCard
          title="Added Usage"
          value={wallet?.topup_balance ?? 0}
          icon={PiggyBank}
          gradient="from-blue-500 to-indigo-600"
          subtitle="Top-up balance"
        />
        <BalanceCard
          title="Total Used"
          value={wallet?.total_credits_used}
          icon={Zap}
          gradient="from-amber-500 to-orange-600"
          subtitle="All-time consumption"
        />
        <BalanceCard
          title="Total Usage Balance"
          value={wallet?.balance}
          icon={WalletIcon}
          gradient="from-violet-500 to-purple-600"
          subtitle="Available for API calls"
        />
      </div>

      {/* Balance Equation Info */}
      {walletSummary && (
        <div className="bg-slate-50 rounded-xl p-4 text-sm">
          <div className="flex flex-wrap items-center gap-2 text-slate-600">
            <span className="font-medium">Balance Equation:</span>
            <span className="font-mono">
              {formatCredits(walletSummary.total_purchased)} (purchased) 
              {walletSummary.total_adjusted >= 0 ? ' + ' : ' - '}
              {formatCredits(Math.abs(walletSummary.total_adjusted || 0))} (adjusted)
              {(walletSummary.total_subscription_bonus || 0) > 0 && ` + ${formatCredits(walletSummary.total_subscription_bonus)} (subscription)`}
              {(walletSummary.total_referral_bonus || 0) > 0 && ` + ${formatCredits(walletSummary.total_referral_bonus)} (referral)`}
              {' - '}
              {formatCredits(walletSummary.total_used)} (used)
              {' = '}
              <span className="font-bold text-emerald-600">{formatCredits(wallet?.balance)}</span>
            </span>
          </div>
        </div>
      )}

      {/* Savings Calculator - Only show for non-top-tier users with usage data */}
      {!isTopTier && savingsData?.savings_by_plan?.length > 0 && savingsData.last_30_days?.tryon_count > 0 && (
        <SavingsCalculator 
          savings={savingsData.savings_by_plan}
          currentPlan={currentPlan}
          onUpgrade={(plan) => openUpgradeModal(plan)}
        />
      )}

      {/* Transaction History */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-2xl border border-slate-200 overflow-hidden"
      >
        {/* Header */}
        <div className="p-4 sm:p-5 border-b border-slate-100">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              Transaction History
              <span className="text-sm font-normal text-slate-500">({totalTransactions})</span>
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-sm transition-colors ${
                  showFilters ? 'bg-blue-50 border-blue-200 text-blue-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Filter className="w-4 h-4" />
                Filters
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              <button
                onClick={handleExportCSV}
                disabled={exporting}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition-colors"
              >
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Export
              </button>
              <button
                onClick={() => fetchTransactions()}
                className="p-1.5 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors"
                title="Refresh"
              >
                <RefreshCw className={`w-4 h-4 ${loadingTransactions ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          {/* Expandable Filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="pt-4 mt-4 border-t border-slate-100 grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {/* Type Filter */}
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Type</label>
                    <select
                      value={typeFilter}
                      onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">All Transactions</option>
                      <option value="credit">Credits Only</option>
                      <option value="debit">Debits Only</option>
                    </select>
                  </div>

                  {/* Date Filter */}
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Date Range</label>
                    <select
                      value={dateFilter}
                      onChange={(e) => { setDateFilter(e.target.value); setPage(1); }}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">All Time</option>
                      <option value="7d">Last 7 Days</option>
                      <option value="30d">Last 30 Days</option>
                      <option value="90d">Last 90 Days</option>
                      <option value="custom">Custom Range</option>
                    </select>
                  </div>

                  {/* Sort By */}
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Sort By</label>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="date">Date</option>
                      <option value="credits">Credits</option>
                      <option value="amount">Amount</option>
                    </select>
                  </div>

                  {/* Sort Order */}
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Order</label>
                    <select
                      value={sortOrder}
                      onChange={(e) => setSortOrder(e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="desc">Newest First</option>
                      <option value="asc">Oldest First</option>
                    </select>
                  </div>

                  {/* Custom Date Range */}
                  {dateFilter === 'custom' && (
                    <>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">From</label>
                        <input
                          type="date"
                          value={customDateStart}
                          onChange={(e) => setCustomDateStart(e.target.value)}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">To</label>
                        <input
                          type="date"
                          value={customDateEnd}
                          onChange={(e) => setCustomDateEnd(e.target.value)}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Transaction List */}
        <div className="divide-y divide-slate-100">
          {loadingTransactions && transactions.length === 0 ? (
            <div className="p-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600" />
            </div>
          ) : transactions.length > 0 ? (
            <>
              {transactions.map((tx, index) => (
                <TransactionRow
                  key={tx.transaction_id || index}
                  tx={tx}
                  onDownloadInvoice={handleDownloadInvoice}
                  downloadingInvoice={downloadingInvoice}
                />
              ))}
            </>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No transactions found</p>
              {(typeFilter !== 'all' || dateFilter !== 'all') && (
                <button
                  onClick={() => { setTypeFilter('all'); setDateFilter('all'); }}
                  className="mt-2 text-blue-600 text-sm hover:underline"
                >
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-slate-100 flex items-center justify-between">
            <p className="text-sm text-slate-500">
              Showing {((page - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(page * ITEMS_PER_PAGE, totalTransactions)} of {totalTransactions}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm hover:bg-slate-50 disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-slate-600">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm hover:bg-slate-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Recharge Modal */}
      <AnimatePresence>
        {showRecharge && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl p-6 max-w-lg w-full relative max-h-[90vh] overflow-y-auto"
              data-testid="recharge-modal"
            >
              <button
                onClick={closeRechargeModal}
                className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>

              {paymentStep === 'select' && (
                <>
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">Add Usage Balance</h2>
                  <p className="text-slate-600 mb-4">Prepaid service value • ₹1 = ₹1 usage balance • Minimum ₹1,000</p>

                  {/* Next tier suggestion */}
                  {nextTier && nextTier.price && (
                    <div className="mb-4 p-3 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-200">
                      <p className="text-sm text-purple-700">
                        <Sparkles className="w-4 h-4 inline mr-1" />
                        <strong>Tip:</strong> Upgrade to {nextTier.name} for ₹{nextTier.price?.toLocaleString()}/mo and get ₹{nextTier.credits?.toLocaleString()} usage balance + lower per-call pricing!
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3 mb-6">
                    {packages.map((pkg) => (
                      <button
                        key={pkg.id}
                        onClick={() => { setSelectedPackage(pkg); setCustomAmount(''); }}
                        className={`p-4 rounded-xl border-2 transition-all relative ${
                          selectedPackage?.id === pkg.id && !customAmount
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`option-${pkg.credits}`}
                      >
                        {pkg.popular && (
                          <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-amber-500 text-xs font-bold rounded-full text-white">
                            POPULAR
                          </span>
                        )}
                        <p className="text-2xl font-bold text-slate-900">₹{pkg.credits.toLocaleString()}</p>
                        <p className="text-slate-500 text-sm">usage balance</p>
                        <p className="text-blue-600 text-sm font-medium mt-1">Pay ₹{pkg.price.toLocaleString()}</p>
                        {pkg.bonus_credits > 0 && (
                          <p className="text-emerald-600 text-xs font-semibold mt-0.5">+₹{pkg.bonus_credits.toLocaleString()} bonus ({pkg.bonus_pct}%)</p>
                        )}
                      </button>
                    ))}
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm text-slate-600 mb-2">Custom amount (min. ₹1,000)</label>
                    <input
                      type="number"
                      value={customAmount}
                      onChange={(e) => { setCustomAmount(e.target.value); setSelectedPackage(null); }}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      placeholder="Enter amount in ₹ (e.g., 2000)"
                      min="1000"
                      data-testid="custom-amount-input"
                    />
                    {currentAmount.error && (
                      <p className="text-red-500 text-sm mt-1">{currentAmount.error}</p>
                    )}
                  </div>

                  <div className="p-4 bg-slate-50 rounded-xl mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-slate-600">Usage Balance to Credit</span>
                      <span className="font-semibold text-slate-900">₹{currentAmount.credits.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-slate-600">Base Amount</span>
                      <span className="text-slate-900">₹{currentAmount.price.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between text-slate-500">
                      <span>GST (18%)</span>
                      <span>+ ₹{gstAmount.toLocaleString()}</span>
                    </div>
                    <div className="border-t border-slate-200 mt-2 pt-2 flex items-center justify-between">
                      <span className="font-medium text-slate-700">Total Payable</span>
                      <span className="text-2xl font-bold text-emerald-600">₹{totalWithGST.toLocaleString()}</span>
                    </div>
                  </div>

                  <button
                    onClick={handlePayment}
                    disabled={currentAmount.credits < 100 || recharging}
                    className="w-full py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="proceed-to-payment-btn"
                  >
                    {recharging ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5" />
                        Pay ₹{totalWithGST.toLocaleString()}
                      </>
                    )}
                  </button>

                  <div className="mt-4 flex items-center justify-center gap-2 text-sm text-slate-500">
                    <ShieldCheck className="w-4 h-4" />
                    Secured by Razorpay • GST Invoice Included
                  </div>
                </>
              )}

              {paymentStep === 'processing' && (
                <div className="text-center py-8">
                  <Loader2 className="w-16 h-16 mx-auto mb-6 text-blue-600 animate-spin" />
                  <h2 className="text-xl font-bold text-slate-900 mb-2">Processing Payment...</h2>
                  <p className="text-slate-600">Please wait while we verify your payment</p>
                </div>
              )}

              {paymentStep === 'success' && (
                <div className="text-center py-8">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", damping: 10 }}
                    className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-500 flex items-center justify-center"
                  >
                    <Check className="w-10 h-10 text-white" />
                  </motion.div>
                  <h2 className="text-xl font-bold text-slate-900 mb-2">Payment Successful!</h2>
                  <p className="text-slate-600 mb-4">Credits have been added to your wallet</p>
                  <div className="text-3xl font-bold text-emerald-600 mb-2">
                    +{currentAmount.credits} Credits
                  </div>
                  <p className="text-sm text-slate-500 flex items-center justify-center gap-2">
                    <FileText className="w-4 h-4" />
                    Invoice sent to your email
                  </p>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Upgrade Modal */}
      <AnimatePresence>
        {showUpgrade && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl p-6 max-w-lg w-full relative max-h-[90vh] overflow-y-auto"
              data-testid="upgrade-modal"
            >
              <button
                onClick={closeUpgradeModal}
                className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>

              {paymentStep === 'select' && (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center">
                      <Rocket className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900">Upgrade Plan</h2>
                      <p className="text-slate-600">Unlock more features & better pricing</p>
                    </div>
                  </div>

                  <div className="space-y-3 mb-6">
                    {/* Only show plans higher than current */}
                    {currentPlan === 'starter' && (() => {
                      const growthPlan = subscriptionPlans.find(p => p.plan_id === 'growth');
                      const effectivePrice = growthPlan?.effective_price ?? growthPlan?.price_monthly_intro ?? growthPlan?.price_monthly ?? 0;
                      const effectiveCredits = growthPlan?.effective_credits ?? growthPlan?.monthly_credits_intro ?? growthPlan?.monthly_credits ?? 0;
                      const tryonCost = growthPlan?.tryon_cost ?? 19;
                      return (
                        <button
                          onClick={() => setSelectedPlan('growth')}
                          className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                            selectedPlan === 'growth' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-0.5 bg-blue-500 text-white text-xs font-semibold rounded">POPULAR</span>
                              <h3 className="text-lg font-bold text-slate-900">Growth</h3>
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-bold text-blue-600">₹{effectivePrice.toLocaleString()}</p>
                              <p className="text-xs text-slate-500">/month</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="flex items-center gap-1 text-slate-600">
                              <Check className="w-4 h-4 text-emerald-500" />
                              {effectiveCredits.toLocaleString()} monthly credits
                            </div>
                            <div className="flex items-center gap-1 text-slate-600">
                              <Check className="w-4 h-4 text-emerald-500" />
                              {tryonCost} credits/VTO
                            </div>
                          </div>
                        </button>
                      );
                    })()}

                    {(currentPlan === 'starter' || currentPlan === 'growth') && (() => {
                      const proPlan = subscriptionPlans.find(p => p.plan_id === 'professional');
                      const effectivePrice = proPlan?.effective_price ?? proPlan?.price_monthly_intro ?? proPlan?.price_monthly ?? 0;
                      const effectiveCredits = proPlan?.effective_credits ?? proPlan?.monthly_credits_intro ?? proPlan?.monthly_credits ?? 0;
                      const tryonCost = proPlan?.tryon_cost ?? 18;
                      return (
                        <button
                          onClick={() => setSelectedPlan('professional')}
                          className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                            selectedPlan === 'professional' ? 'border-purple-500 bg-purple-50' : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="px-2 py-0.5 bg-purple-500 text-white text-xs font-semibold rounded">BEST VALUE</span>
                              <h3 className="text-lg font-bold text-slate-900">Professional</h3>
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-bold text-purple-600">₹{effectivePrice.toLocaleString()}</p>
                              <p className="text-xs text-slate-500">/month</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="flex items-center gap-1 text-slate-600">
                              <Check className="w-4 h-4 text-emerald-500" />
                              {effectiveCredits.toLocaleString()} monthly credits
                            </div>
                            <div className="flex items-center gap-1 text-slate-600">
                              <Check className="w-4 h-4 text-emerald-500" />
                              {tryonCost} credits/VTO
                            </div>
                          </div>
                        </button>
                      );
                    })()}
                  </div>

                  <button
                    onClick={handleSubscriptionUpgrade}
                    disabled={!selectedPlan || recharging}
                    className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-medium hover:from-purple-700 hover:to-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="confirm-upgrade-btn"
                  >
                    {recharging ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5" />
                        Upgrade to {subscriptionPlans.find(p => p.plan_id === selectedPlan)?.name || selectedPlan}
                      </>
                    )}
                  </button>

                  <p className="text-center text-xs text-slate-500 mt-3">
                    30-day billing cycle • Cancel anytime • GST Invoice included
                  </p>
                </>
              )}

              {paymentStep === 'processing' && (
                <div className="text-center py-8">
                  <Loader2 className="w-16 h-16 mx-auto mb-6 text-purple-600 animate-spin" />
                  <h2 className="text-xl font-bold text-slate-900 mb-2">Processing Upgrade...</h2>
                  <p className="text-slate-600">Please wait while we activate your plan</p>
                </div>
              )}

              {paymentStep === 'success' && (() => {
                const selectedPlanData = subscriptionPlans.find(p => p.plan_id === selectedPlan);
                const effectiveCredits = selectedPlanData?.effective_credits ?? selectedPlanData?.monthly_credits_intro ?? selectedPlanData?.monthly_credits ?? 0;
                return (
                  <div className="text-center py-8">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", damping: 10 }}
                      className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center"
                    >
                      <Check className="w-10 h-10 text-white" />
                    </motion.div>
                    <h2 className="text-xl font-bold text-slate-900 mb-2">Welcome to {selectedPlanData?.name || selectedPlan}!</h2>
                    <p className="text-slate-600 mb-4">Your plan has been activated</p>
                    <div className="text-3xl font-bold text-purple-600 mb-2">
                      +{effectiveCredits.toLocaleString()} Credits
                    </div>
                    <p className="text-sm text-slate-500">Added to your wallet</p>
                  </div>
                );
              })()}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Wallet;
